import AdjustIcon from '@mui/icons-material/Adjust'
import React from 'react'
import styled from 'styled-components'
import { Card } from 'sutra/components/Card/Card'
import { brand, gray } from 'sutra/tokens/colors'
import { spacing } from 'sutra/tokens/spacing'

import { ENTITY_LABELS } from 'src/components/agent-forms/benchmark-entity-labels'
import { ZERO_STATS } from 'src/components/agent-forms/benchmark-stat-format'
import type { BenchmarkingEntityType } from 'src/components/agent-forms/BenchmarkingAgentConfigForm'
import { FocusEntityPicker } from 'src/components/agent-forms/BenchmarkUniversePanel/FocusEntityPicker'
import { GroupSummaryRow } from 'src/components/agent-forms/BenchmarkUniversePanel/GroupSummaryRow'
import type { BenchmarkingGroupStats } from 'src/components/agent-forms/use-benchmark-compare-stats'
import type { UseFocusEntitySelectionResult } from 'src/components/agent-forms/use-focus-entity-selection'

export interface BenchmarkFocusCardProps {
  entityType: BenchmarkingEntityType | null
  focus: UseFocusEntitySelectionResult
  /** `null` while `focusGroupFilters` is empty — the panel renders
   * `ZERO_STATS` in that case rather than the universe's own numbers. */
  stats: BenchmarkingGroupStats | null
  comparedTo: BenchmarkingGroupStats | null
  statsLoading: boolean
  statsUnavailable: boolean
  /** Server-derived label for the trailing analysis window (e.g. "last 91
   * days") — see `BenchmarkingCompareStatsResponse.date_window_label`. */
  dateWindowLabel: string | null
}

/**
 * The nested navy-tinted card: a dynamic "Compare these brands…" /
 * "Compare these products…" title (leading MUI `Adjust` icon), one chip input holding the focused
 * values plus the "+ add" typeahead, and its own stats block — laid out as
 * two columns (title + chip input on the left, stats sized to their own
 * content on the right) rather than a title/stats header row with the
 * chip input full-width underneath. The Brands/Products entity toggle lives
 * above the Universe card in the parent form, not here — this card only
 * reads `entityType` to pick its title and labels. Renders a hint instead of
 * the picker when no entity type is chosen
 * (reachable only pre-hydration on the edit/staff surfaces — the create
 * wizard defaults to `'brand'`).
 */
export const BenchmarkFocusCard: React.FC<BenchmarkFocusCardProps> = ({
  entityType,
  focus,
  stats,
  comparedTo,
  statsLoading,
  statsUnavailable,
  dateWindowLabel,
}) => {
  const labels = entityType ? ENTITY_LABELS[entityType] : null
  const focusTitle = labels
    ? `Compare these ${labels.section.toLowerCase()}…`
    : 'Compare these brands or products…'

  return (
    <StyledCard>
      <ContentRow>
        <LeftColumn>
          <Title>
            <AdjustIcon sx={{ fontSize: 18 }} />
            {focusTitle}
          </Title>
          {labels ? (
            <FocusEntityPicker
              addLabel={labels.addLabel}
              values={focus.values}
              staleValues={focus.staleValues}
              options={focus.availableOptions}
              isLoading={focus.isLoading}
              onAdd={focus.addValue}
              onRemove={focus.removeValue}
              noOptionsLabel={`All ${labels.section.toLowerCase()} added`}
            />
          ) : (
            <Hint>Choose brands or products to focus on.</Hint>
          )}
        </LeftColumn>
        <RightColumn>
          <GroupSummaryRow
            entityType={entityType}
            stats={stats ?? ZERO_STATS}
            comparedTo={comparedTo}
            isLoading={statsLoading}
            isUnavailable={statsUnavailable}
            dateWindowLabel={dateWindowLabel}
          />
        </RightColumn>
      </ContentRow>
    </StyledCard>
  )
}

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]}px;
  background: ${brand.stillNight[100]};
  border-color: ${brand.stillNight[200]};
  box-shadow: none;
`

const ContentRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[4]}px;
`

const LeftColumn = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]}px;
`

const RightColumn = styled.div`
  flex: 0 0 auto;
`

const Title = styled.h4`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Poppins', system-ui, sans-serif;
  font-weight: 600;
  font-size: 14px;
  color: ${brand.stillNight[800]};
  margin: 0;
`

const Hint = styled.p`
  font-size: 13px;
  color: ${gray[600]};
  margin: 0;
`
